import type { Frequency, RecurringItem } from '@/api/recurring'

export type Shade = 0 | 1 | 2 | 3 | 4 | 5

const COMING_UP_DAYS = 14

/** Five steps of green, scaled to the month's biggest spending day; 0 for a day with none. */
export function shade(expense: number, max: number): Shade {
  if (expense <= 0 || max <= 0) return 0
  return Math.min(5, Math.max(1, Math.ceil((expense / max) * 5))) as Shade
}

function parse(d: string): Date {
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day)
}

export function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function addDays(d: string, n: number): string {
  const x = parse(d)
  x.setDate(x.getDate() + n)
  return iso(x)
}

const MONTHS: Record<Exclude<Frequency, 'WEEKLY'>, number> = {
  MONTHLY: 1,
  QUARTERLY: 3,
  HALF_YEARLY: 6,
  YEARLY: 12,
}

// One period after `from`, the way the server advances a reminder (Java plusMonths): the day is
// clamped to the target month's length and stays clamped, so 31 Jan → 28 Feb → 28 Mar.
function next(from: string, frequency: Frequency): string {
  if (frequency === 'WEEKLY') return addDays(from, 7)
  const s = parse(from)
  const target = new Date(s.getFullYear(), s.getMonth() + MONTHS[frequency], 1)
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
  target.setDate(Math.min(s.getDate(), last))
  return iso(target)
}

/** Each reminder's due dates inside [from, to]: its next due date, then every period after it. */
export function occurrences(
  items: RecurringItem[],
  from: string,
  to: string
): Map<string, RecurringItem[]> {
  const out = new Map<string, RecurringItem[]>()
  for (const item of items) {
    for (let d = item.nextDueDate; d <= to; d = next(d, item.frequency)) {
      if (d >= from) out.set(d, [...(out.get(d) ?? []), item])
    }
  }
  return new Map([...out.entries()].sort(([a], [b]) => a.localeCompare(b)))
}

/** Late reminders first (at the date they were due), then everything due in the next two weeks. */
export function comingUp(
  items: RecurringItem[],
  today: string
): { date: string; items: RecurringItem[] }[] {
  const late = items.filter((i) => i.nextDueDate < today)
  const lateGroups = [...occurrences(late, '0000-01-01', addDays(today, -1)).entries()]
    .filter(([date]) => late.some((i) => i.nextDueDate === date))
    .map(([date, list]) => ({ date, items: list.filter((i) => i.nextDueDate === date) }))
  const ahead = [...occurrences(items, today, addDays(today, COMING_UP_DAYS)).entries()].map(
    ([date, list]) => ({ date, items: list })
  )
  return [...lateGroups, ...ahead]
}

/** "Mon, 12 Oct" — the heading for one day. */
export function dayTitle(date: string): string {
  return parse(date).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}
